'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Inviter,
  Invitation,
  Registerer,
  RegistererState,
  SessionState,
  TransportState,
  UserAgent,
  Web,
} from 'sip.js';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { classifyConnectError } from './classify-connect-error';
import { atrasoReconexao } from './webphone-reconnect';
import type {
  WebphoneState,
  ActiveCall,
  WebphoneCredentials,
  WebphoneError,
} from './webphone-types';

interface UseWebphoneReturn {
  state: WebphoneState;
  call: ActiveCall | null;
  error: WebphoneError | null;
  /** Ramal local registrado no SIP (extraído do username das creds). */
  extension: string | null;
  connect: () => Promise<void>;
  disconnect: () => void;
  dial: (number: string, options?: { remoteName?: string }) => Promise<void>;
  acceptIncoming: () => void;
  rejectIncoming: () => void;
  hangup: () => void;
  toggleMute: () => void;
  toggleHold: () => void;
  sendDtmf: (digit: string) => void;
  /** Mock helper pra simular incoming call (so funciona em mock mode) */
  simulateIncoming: (number: string, name?: string) => void;
}

const MOCK_MODE = () => process.env.NEXT_PUBLIC_VOICE_MOCK === 'true';

/**
 * Hook que gerencia estado de webphone WebRTC via SIP.js.
 *
 * Dois caminhos:
 *  - NEXT_PUBLIC_VOICE_MOCK=true  -> simulacao via setTimeout (UI dev sem motor)
 *  - senao                        -> SIP.js real (UserAgent + Registerer + Inviter + audio)
 *
 * Em producao, assim que motor expoe /voice/credentials + WSS, basta unset da env
 * que o hook conecta de verdade.
 */
/**
 * Mapeia o código de rejeição SIP (do INVITE) para uma mensagem clara pro
 * atendente — em vez do "rejected" cru.
 *
 * O código SIP sozinho é AMBÍGUO: o Asterisk devolve 480 tanto pra "offline"
 * quanto pra "recusou" ou "não atendeu". Por isso usamos o sinal `rang` (a
 * chamada chegou a tocar = recebeu 180 Ringing antes do reject): se tocou, o
 * ramal foi alcançado → recusa/não-atendida (NÃO é offline); se nem tocou →
 * indisponível/sem rota de verdade. (Precisão total — recusou vs não-atendeu —
 * só com o motivo Q.850 do evento de hangup do motor; isto cobre o comum.)
 */
function sipRejectMessage(code: number, _reason: string | undefined, rang: boolean): string {
  // Se a chamada chegou a tocar (rang=true), o destino FOI alcançado — então é
  // "não atendida/recusada", NUNCA "ocupado" nem "indisponível". (Caso clássico:
  // ligar pro próprio número e não atender — tocava e voltava 486 = encerrada.)
  // Também evitamos a palavra "ramal", que o agente confunde com o próprio.
  if (rang) {
    return 'Ligação não atendida ou recusada.';
  }
  // Não tocou: o destino não foi alcançado — mensagem específica por código.
  switch (code) {
    case 486: // Busy Here — ocupado de verdade
      return 'O número chamado está ocupado.';
    case 408: // Request Timeout
      return 'Sem resposta — ninguém atendeu.';
    case 603: // Decline explícito
      return 'Chamada recusada.';
    case 404: // Not Found — número sem rota / inexistente
      return 'Número não encontrado ou indisponível.';
    case 480: // Temporarily Unavailable
      return 'Número indisponível no momento.';
    case 488: // Not Acceptable Here
    case 606: // Not Acceptable
      return 'Falha de áudio na chamada.';
    case 503: // Service Unavailable
      return 'Serviço de telefonia indisponível no momento.';
    default:
      return 'Não foi possível completar a chamada.';
  }
}

export function useWebphone(): UseWebphoneReturn {
  const [state, setState] = useState<WebphoneState>('disconnected');
  const [call, setCall] = useState<ActiveCall | null>(null);
  const [error, setError] = useState<WebphoneError | null>(null);

  const uaRef = useRef<UserAgent | null>(null);
  const registererRef = useRef<Registerer | null>(null);
  const sessionRef = useRef<Inviter | Invitation | null>(null);
  const credsRef = useRef<WebphoneCredentials | null>(null);
  // Marca teardown intencional (disconnect/pagehide) pra distinguir de queda
  // espontânea do WS no listener de transport (UX-2).
  const intentionalStopRef = useRef(false);

  // #490: reconexão automática do transporte. O SIP.js não reconecta sozinho —
  // sem isso, o WS cai, o Asterisk perde o contato do AOR e a fila pula o agente
  // EM SILÊNCIO até ele reparar no aviso e apertar F5.
  const tentativaReconexaoRef = useRef(0);
  const timerReconexaoRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Quebra o ciclo agendar -> tentar -> agendar sem recriar os callbacks.
  const agendarReconexaoRef = useRef<() => void>(() => {});

  // Timers do mock mode
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const stateRef = useRef<WebphoneState>('disconnected');
  // Guarda contra double-click rapido em toggleHold (re-INVITE em flight)
  const pendingHoldRef = useRef(false);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const cleanup = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  /** Para de tentar reconectar e zera o backoff (teardown ou sucesso). */
  const cancelarReconexao = useCallback(() => {
    if (timerReconexaoRef.current) {
      clearTimeout(timerReconexaoRef.current);
      timerReconexaoRef.current = null;
    }
    tentativaReconexaoRef.current = 0;
  }, []);

  /**
   * #490 — reconecta o transporte e re-REGISTRA. Enquanto não voltar, o ramal
   * não existe pro Asterisk e a fila o pula sem log, sem evento e sem métrica.
   */
  const tentarReconectar = useCallback(async () => {
    const ua = uaRef.current;
    if (!ua || intentionalStopRef.current) return;
    try {
      if (ua.transport.state !== TransportState.Connected) {
        await ua.transport.connect();
      }
      // O REGISTER precisa ser refeito: o contato do AOR morreu junto com o
      // socket, e reconectar o transporte sozinho não devolve o ramal pra fila.
      if (registererRef.current) {
        await registererRef.current.register();
      }
      tentativaReconexaoRef.current = 0;
      setError(null);
    } catch {
      // Falhou: agenda a próxima com o intervalo já dobrado.
      agendarReconexaoRef.current();
    }
  }, []);

  const agendarReconexao = useCallback(() => {
    if (intentionalStopRef.current) return;
    if (timerReconexaoRef.current) return; // já há uma tentativa na fila
    const tentativa = (tentativaReconexaoRef.current += 1);
    timerReconexaoRef.current = setTimeout(() => {
      timerReconexaoRef.current = null;
      void tentarReconectar();
    }, atrasoReconexao(tentativa));
  }, [tentarReconectar]);

  useEffect(() => {
    agendarReconexaoRef.current = agendarReconexao;
  }, [agendarReconexao]);

  /**
   * Rede de volta ou aba reativada: tenta JÁ, zerando o backoff. Cobre o caso
   * mais comum do parque — notebook que suspendeu ou Wi-Fi que oscilou — em que
   * esperar os 30s do teto seria perder ligação à toa.
   */
  useEffect(() => {
    const reconectarAgora = () => {
      if (intentionalStopRef.current) return;
      const ua = uaRef.current;
      if (!ua || ua.transport.state === TransportState.Connected) return;
      cancelarReconexao();
      void tentarReconectar();
    };
    const aoFicarVisivel = () => {
      if (document.visibilityState === 'visible') reconectarAgora();
    };
    window.addEventListener('online', reconectarAgora);
    document.addEventListener('visibilitychange', aoFicarVisivel);
    return () => {
      window.removeEventListener('online', reconectarAgora);
      document.removeEventListener('visibilitychange', aoFicarVisivel);
    };
  }, [cancelarReconexao, tentarReconectar]);

  const cleanupSip = useCallback(async (reason: string) => {
    // eslint-disable-next-line no-console
    console.warn('[webphone] cleanupSip START — reason:', reason, 'sessionState:', sessionRef.current?.state);
    // #490: dispara o REGISTER expires=0 ANTES de qualquer `await`. No
    // `pagehide` o navegador não espera promise, então o que não foi pro socket
    // agora não vai mais — e era isso que acontecia: o unregister existia, mas
    // ficava atrás de `cancel()`/`bye()` e a página morria antes. Medido na
    // Minas: 203 REGISTER expires=0 em 4h51, todos de telefone de mesa, ZERO de
    // webphone. Sem ele, "fechou a aba" e "caiu a conexão" viram o mesmo evento
    // pro servidor, e só o qualify (60s) devolve o ramal pra fila.
    // MEDIDO, não deduzido (13/09/2026, Asterisk de dev com `pjsip set logger on`):
    // fechando a aba com o ramal OCIOSO — o caminho em que o `dispose()` abaixo
    // vem logo atrás, sem await — o servidor RECEBE o desregistro:
    //
    //   CSeq: 4 REGISTER ... Contact: <sip:...;transport=ws>;expires=0
    //
    // mesmo Call-ID do registro. Ou seja, o SIP.js escreve o REGISTER no socket
    // antes de o `dispose()` poder abortá-lo. Medido na versão **0.21.2** (a que
    // o package.json declara e o node_modules resolve) — no log SIP ela se
    // anuncia como `User-Agent: SIP.js/0.21.1`, porque o `version.js` da própria
    // biblioteca ficou desatualizado nesse release; confira no package.json, não
    // no User-Agent. Isso não está amarrado por
    // teste unitário — é comportamento da biblioteca. Se um upgrade do SIP.js
    // tornar esse envio assíncrono, o desregistro volta a se perder EM SILÊNCIO;
    // a verificação é repetir a medição acima e procurar `expires=0` no log
    // (atenção: o SIP.js manda o expires como parâmetro do Contact, NUNCA como
    // header `Expires:` — grep no header dá falso negativo).
    const regParaFechar = registererRef.current;
    if (regParaFechar) {
      try {
        void regParaFechar.unregister().catch(() => {});
      } catch {
        // Registerer em estado inválido: o teardown abaixo segue mesmo assim.
      }
    }
    try {
      if (sessionRef.current) {
        const s = sessionRef.current;
        if (s instanceof Inviter && s.state !== SessionState.Terminated) {
          // eslint-disable-next-line no-console
          console.warn('[webphone] cleanupSip -> cancel() inviter');
          await s.cancel().catch(() => {});
        }
        if (s.state === SessionState.Established) {
          // eslint-disable-next-line no-console
          console.warn('[webphone] cleanupSip -> bye() session');
          await s.bye().catch(() => {});
        }
        sessionRef.current = null;
      }
      if (registererRef.current) {
        // O unregister já foi disparado no topo, sem await (ver nota lá).
        registererRef.current.dispose();
        registererRef.current = null;
      }
      if (uaRef.current) {
        await uaRef.current.stop().catch(() => {});
        uaRef.current = null;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[webphone] cleanup error', err);
    }
  }, []);

  // IMPORTANTE: nao fazer teardown SIP em todo unmount.
  // Strict Mode em dev faz mount->unmount->mount em <100ms.
  // Se um remount durante uma chamada disparasse cancel/bye, a UI fica
  // fora de sync com o motor (motor segue rodando, UI volta pra registered).
  // Teardown SIP so via disconnect() explicito ou pagehide.
  useEffect(() => {
    const onPageHide = () => {
      intentionalStopRef.current = true;
      cancelarReconexao(); // a aba esta indo embora: nao adianta reagendar
      void cleanupSip('pagehide');
    };
    // Avisa user antes de fechar/recarregar quando ha call ativa.
    // Browser mostra dialog "Sair desta pagina?" — F5 acidental nao derruba.
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      const inCall =
        stateRef.current === 'in_call' ||
        stateRef.current === 'ringing' ||
        stateRef.current === 'calling';
      if (inCall) {
        e.preventDefault();
        e.returnValue = ''; // texto custom ignorado por browsers modernos
      }
    };
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      cleanup(); // so timers
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [cleanup, cleanupSip]);

  // ---------- AUDIO ----------

  const setupRemoteMedia = useCallback((session: Inviter | Invitation) => {
    const sdh = session.sessionDescriptionHandler as Web.SessionDescriptionHandler | undefined;
    const pc = sdh?.peerConnection;
    if (!pc) return;

    const remoteStream = new MediaStream();
    pc.getReceivers().forEach((receiver) => {
      if (receiver.track) remoteStream.addTrack(receiver.track);
    });

    let audio = document.getElementById('webphone-audio') as HTMLAudioElement | null;
    if (!audio) {
      audio = document.createElement('audio');
      audio.id = 'webphone-audio';
      audio.autoplay = true;
      audio.style.display = 'none';
      document.body.appendChild(audio);
    }
    audio.srcObject = remoteStream;
    audio.play().catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[webphone] audio play falhou', err);
    });
  }, []);

  // ---------- INCOMING (SIP.js delegate) ----------

  const handleInvite = useCallback(
    (invitation: Invitation) => {
      const remoteUri = invitation.remoteIdentity.uri;
      const number = remoteUri.user ?? 'desconhecido';
      const displayName = invitation.remoteIdentity.displayName;

      sessionRef.current = invitation;
      const newCall: ActiveCall = {
        id: invitation.request.callId ?? `inv-${Date.now()}`,
        direction: 'inbound',
        remoteNumber: number,
        remoteName: displayName || undefined,
        startedAt: Date.now(),
        answeredAt: null,
        muted: false,
        onHold: false,
      };
      setCall(newCall);
      setState('ringing');

      invitation.stateChange.addListener((s) => {
        if (s === SessionState.Established) {
          setupRemoteMedia(invitation);
          setCall((c) => (c ? { ...c, answeredAt: Date.now() } : null));
          setState('in_call');
        } else if (s === SessionState.Terminated) {
          sessionRef.current = null;
          setState('registered');
          setCall(null);
        }
      });
    },
    [setupRemoteMedia],
  );

  // ---------- CONNECT ----------

  const connect = useCallback(async () => {
    setError(null);
    setState('connecting');
    intentionalStopRef.current = false;

    if (MOCK_MODE()) {
      // Tenta GET creds, mas ignora erro (motor offline nao quebra mock)
      try {
        const creds = await api.request<WebphoneCredentials>('/telefonia/credenciais-sip');
        credsRef.current = creds;
      } catch {
        // eslint-disable-next-line no-console
        console.warn('[webphone] mock mode — motor offline, seguindo sem creds');
      }
      const t = setTimeout(() => setState('registered'), 1500);
      timersRef.current.push(t);
      return;
    }

    try {
      const creds = await api.request<WebphoneCredentials>('/telefonia/credenciais-sip');
      credsRef.current = creds;

      const uri = UserAgent.makeURI(creds.sip_uri);
      if (!uri) throw new Error('telefone_indisponivel');

      const debug = process.env.NEXT_PUBLIC_VOICE_DEBUG === 'true';

      const ua = new UserAgent({
        uri,
        authorizationUsername: creds.username,
        authorizationPassword: creds.secret,
        transportOptions: {
          server: creds.ws_url,
          traceSip: debug,
        },
        delegate: {
          onInvite: (invitation) => handleInvite(invitation),
        },
        sessionDescriptionHandlerFactoryOptions: {
          iceGatheringTimeout: 500,
          peerConnectionConfiguration: {
            iceServers: creds.ice_servers,
          },
        },
        logBuiltinEnabled: debug,
        logLevel: debug ? 'debug' : 'warn',
      });

      uaRef.current = ua;

      // Transport state — captura WS connect/disconnect/error
      ua.transport.stateChange.addListener((tState) => {
        if (debug) {
          // eslint-disable-next-line no-console
          console.log('[webphone] transport ->', tState);
        }
        // WS caiu sozinho (não foi teardown nosso) com o webphone em uso: avisa
        // o agente E reconecta. O SIP.js não reconecta o transport sozinho, e
        // até a #490 nós também não: o agente ficava "registered" na tela, o
        // Asterisk sem contato no AOR, e a fila o pulava em silêncio até ele
        // reparar no aviso e apertar F5 (na Minas, ramais passaram o dia assim).
        if (
          tState === TransportState.Disconnected &&
          !intentionalStopRef.current &&
          stateRef.current !== 'disconnected' &&
          stateRef.current !== 'connecting' &&
          stateRef.current !== 'failed'
        ) {
          setError({ code: 'network', message: 'Conexão perdida. Reconectando o telefone…' });
          setState('failed');
        }
        // Fora do `if` de propósito: o guarda acima é só pra MENSAGEM (não
        // repetir aviso). A reconexão tem que acontecer em toda queda
        // espontânea — inclusive quando o estado já era 'failed', que é
        // exatamente o caso de uma tentativa anterior ter falhado.
        if (tState === TransportState.Disconnected && !intentionalStopRef.current) {
          agendarReconexaoRef.current();
        }
      });

      await ua.start();
      if (debug) {
        // eslint-disable-next-line no-console
        console.log('[webphone] UA started, ws =', creds.ws_url, 'uri =', creds.sip_uri);
      }

      const registerer = new Registerer(ua);
      registererRef.current = registerer;

      registerer.stateChange.addListener((s) => {
        // eslint-disable-next-line no-console
        console.log('[webphone] registerer ->', s);
        if (s === RegistererState.Registered) {
          setState('registered');
          // Reconexão deu certo: tira o aviso de queda da tela.
          setError(null);
          tentativaReconexaoRef.current = 0;
        } else if (s === RegistererState.Unregistered) {
          if (stateRef.current !== 'disconnected') {
            setState('failed');
            setError({ code: 'register_failed', message: 'O telefone foi desconectado.' });
          }
        }
      });

      await registerer.register({
        requestDelegate: {
          onAccept: (response) => {
            // eslint-disable-next-line no-console
            console.log(
              '[webphone] REGISTER accepted',
              response.message.statusCode,
              response.message.reasonPhrase,
            );
          },
          onReject: (response) => {
            const status = response.message.statusCode;
            const reason = response.message.reasonPhrase;
            // eslint-disable-next-line no-console
            console.error('[webphone] REGISTER rejected', status, reason);
            if (debug) {
              // eslint-disable-next-line no-console
              console.error(
                '[webphone] REGISTER rejected (debug)',
                '\n  → URI:',
                creds.sip_uri,
                '\n  → user:',
                creds.username,
                '\n  → ws:',
                creds.ws_url,
              );
            }
            // Mensagem genérica pro atendente — sem expor a stack (SIP/AOR/endpoint).
            // O status/motivo técnico fica só no console acima (dev).
            setError({
              code: 'register_failed',
              message: 'Não foi possível conectar o telefone. Tente novamente em instantes.',
            });
            setState('failed');
          },
        },
      });
    } catch (err) {
      const raw = (err as Error).message ?? '';
      // eslint-disable-next-line no-console
      console.warn('[webphone] connect falhou:', raw);
      const kind = classifyConnectError(err);
      if (kind === 'no_extension') {
        // #178: agente sem ramal vinculado (400 sem sync / 404-422 do motor).
        // Não é erro pro atendente — o sticky se esconde e o socket não abre.
        setError({ code: 'no_extension', message: 'Sem ramal vinculado.' });
      } else {
        // Sem detalhe técnico pro atendente — o erro cru fica só no console.
        setError({
          code: 'no_creds',
          message:
            kind === 'unavailable'
              ? 'Telefonia ainda não está disponível. Tente novamente em instantes.'
              : 'Não foi possível conectar o telefone. Tente novamente.',
        });
      }
      setState('failed');
    }
  }, [handleInvite]);

  // ---------- DISCONNECT ----------

  const disconnect = useCallback(() => {
    intentionalStopRef.current = true;
    cancelarReconexao(); // teardown pedido pelo agente: para de insistir
    cleanup();
    void cleanupSip('disconnect-button');
    setState('disconnected');
    setCall(null);
  }, [cancelarReconexao, cleanup, cleanupSip]);

  // ---------- DIAL ----------

  const dial = useCallback(
    async (number: string, options?: { remoteName?: string }) => {
      if (stateRef.current !== 'registered') {
        setError({ code: 'invite_failed', message: 'O telefone ainda não está pronto.' });
        return;
      }

      const newCall: ActiveCall = {
        id: `call-${Date.now()}`,
        direction: 'outbound',
        remoteNumber: number,
        remoteName: options?.remoteName,
        startedAt: Date.now(),
        answeredAt: null,
        muted: false,
        onHold: false,
      };
      setCall(newCall);
      setState('calling');

      if (MOCK_MODE()) {
        // calling 1s -> ringing 2s -> answered (70% chance)
        const t1 = setTimeout(() => setState('ringing'), 1000);
        const t2 = setTimeout(() => {
          if (Math.random() > 0.3) {
            setCall((c) => (c ? { ...c, answeredAt: Date.now() } : null));
            setState('in_call');
          } else {
            setError({ code: 'invite_failed', message: 'Sem resposta' });
            setState('registered');
            setCall(null);
          }
        }, 3500);
        timersRef.current.push(t1, t2);
        return;
      }

      const ua = uaRef.current;
      const creds = credsRef.current;
      if (!ua || !creds) {
        setError({ code: 'invite_failed', message: 'O telefone não está conectado.' });
        setState('registered');
        setCall(null);
        return;
      }

      try {
        // Extrai dominio do registrar_uri pro SIP URI de destino
        const registrarUri = UserAgent.makeURI(creds.registrar_uri);
        const domain = registrarUri?.host ?? 'localhost';

        const target = UserAgent.makeURI(`sip:${number}@${domain}`);
        if (!target) throw new Error('numero invalido');

        const inviter = new Inviter(ua, target);
        sessionRef.current = inviter;
        const callId = inviter.request.callId;
        // eslint-disable-next-line no-console
        console.log('[webphone] dial -> Inviter created, callId:', callId, 'target:', `sip:${number}@${domain}`);

        inviter.stateChange.addListener((s) => {
          const ts = new Date().toISOString().substr(11, 12);
          // eslint-disable-next-line no-console
          console.log(`[webphone ${ts}] inviter state ->`, s, 'callId:', callId);

          if (s === SessionState.Establishing) {
            setState('ringing');
          } else if (s === SessionState.Established) {
            setupRemoteMedia(inviter);
            setCall((c) => (c ? { ...c, answeredAt: Date.now() } : null));
            setState('in_call');
          } else if (s === SessionState.Terminated) {
            // eslint-disable-next-line no-console
            console.warn(
              `[webphone ${ts}] SESSION TERMINATED — clearing UI`,
              '\n  callId:',
              callId,
              '\n  sessionRef matches?',
              sessionRef.current === inviter,
              '\n  current state was:',
              stateRef.current,
            );
            // So limpa UI se for a sessao corrente — evita race com double-mount
            if (sessionRef.current === inviter) {
              sessionRef.current = null;
              setState('registered');
              setCall(null);
            }
          }
        });

        // `rang` = a chamada chegou a tocar o destino (recebeu 180 Ringing).
        // Usado pra desambiguar o reject: tocou = recusa/não-atende, não offline.
        let rang = false;
        // Log delegate de respostas SIP do INVITE
        await inviter.invite({
          sessionDescriptionHandlerOptions: {
            constraints: { audio: true, video: false },
          },
          requestDelegate: {
            onProgress: (response) => {
              // 180 Ringing = um device tocou (vs 183 early media, que a fila
              // pode mandar sem tocar ninguém — por isso checamos 180 explícito).
              if (response.message.statusCode === 180) rang = true;
              // eslint-disable-next-line no-console
              console.log(
                '[webphone] INVITE progress',
                response.message.statusCode,
                response.message.reasonPhrase,
              );
            },
            onAccept: (response) => {
              // eslint-disable-next-line no-console
              console.log(
                '[webphone] INVITE accepted',
                response.message.statusCode,
                response.message.reasonPhrase,
              );
            },
            onReject: (response) => {
              const code = response.message.statusCode ?? 0;
              const reason = response.message.reasonPhrase;
              // 487 "Request Terminated" = NÓS cancelamos (clicou desligar antes
              // de completar) — não é falha, não avisa.
              if (code === 487) {
                // eslint-disable-next-line no-console
                console.info('[webphone] INVITE cancelado', code, reason);
                return;
              }
              const msg = sipRejectMessage(code, reason, rang);
              // eslint-disable-next-line no-console
              console.warn('[webphone] INVITE rejected', code, reason, 'rang:', rang, '->', msg);
              if (rang) {
                // Tocou e não completou (não atendida/recusada) = resultado normal
                // da chamada, não erro técnico: avisa de forma neutra e NÃO abre o
                // painel de erro com "tentar novamente".
                toast(msg);
              } else {
                toast.error(msg);
                setError({ code: 'invite_failed', message: msg });
              }
            },
          },
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[webphone] dial CATCH', err);
        setError({ code: 'invite_failed', message: (err as Error).message });
        setState('registered');
        setCall(null);
      }
    },
    [setupRemoteMedia],
  );

  // ---------- SIMULATE INCOMING (mock only) ----------

  const simulateIncoming = useCallback((number: string, name?: string) => {
    if (stateRef.current !== 'registered') return;
    if (!MOCK_MODE()) return; // real incoming vem do delegate.onInvite

    const newCall: ActiveCall = {
      id: `incoming-mock-${Date.now()}`,
      direction: 'inbound',
      remoteNumber: number,
      remoteName: name,
      startedAt: Date.now(),
      answeredAt: null,
      muted: false,
      onHold: false,
    };
    setCall(newCall);
    setState('ringing');

    const t = setTimeout(() => {
      if (stateRef.current === 'ringing') {
        setState('registered');
        setCall(null);
      }
    }, 30_000);
    timersRef.current.push(t);
  }, []);

  // ---------- ACCEPT / REJECT ----------

  const acceptIncoming = useCallback(async () => {
    if (stateRef.current !== 'ringing' || !call || call.direction !== 'inbound') return;

    // Mock path (sem session SIP real)
    if (MOCK_MODE() && !sessionRef.current) {
      setCall((c) => (c ? { ...c, answeredAt: Date.now() } : null));
      setState('in_call');
      return;
    }

    const session = sessionRef.current;
    if (!session || !(session instanceof Invitation)) return;

    try {
      await session.accept({
        sessionDescriptionHandlerOptions: {
          constraints: { audio: true, video: false },
        },
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[webphone] accept falhou', err);
      setError({ code: 'invite_failed', message: (err as Error).message });
    }
  }, [call]);

  const rejectIncoming = useCallback(async () => {
    if (stateRef.current !== 'ringing') return;

    if (MOCK_MODE() && !sessionRef.current) {
      cleanup();
      setState('registered');
      setCall(null);
      return;
    }

    const session = sessionRef.current;
    if (session && session instanceof Invitation) {
      await session.reject().catch(() => {});
    }
    sessionRef.current = null;
    setState('registered');
    setCall(null);
  }, [cleanup]);

  // ---------- HANGUP ----------

  const hangup = useCallback(async () => {
    // eslint-disable-next-line no-console
    console.warn(
      '[webphone] hangup CALLED — sessionState:',
      sessionRef.current?.state,
      'uiState:',
      stateRef.current,
      '\n  STACK:',
      new Error().stack?.split('\n').slice(2, 6).join('\n'),
    );

    if (MOCK_MODE() && !sessionRef.current) {
      cleanup();
      setState('registered');
      setCall(null);
      return;
    }

    const session = sessionRef.current;
    if (!session) {
      setState('registered');
      setCall(null);
      return;
    }

    try {
      if (
        session instanceof Inviter &&
        session.state !== SessionState.Terminated &&
        session.state !== SessionState.Established
      ) {
        // eslint-disable-next-line no-console
        console.warn('[webphone] hangup -> cancel() (state was', session.state, ')');
        await session.cancel();
      } else if (session.state === SessionState.Established) {
        // eslint-disable-next-line no-console
        console.warn('[webphone] hangup -> bye() (state was Established)');
        await session.bye();
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[webphone] hangup error', err);
    } finally {
      sessionRef.current = null;
      setState('registered');
      setCall(null);
    }
  }, [cleanup]);

  // ---------- MUTE / HOLD / DTMF ----------

  const toggleMute = useCallback(() => {
    if (!call) return;
    const newMuted = !call.muted;
    setCall((c) => (c ? { ...c, muted: newMuted } : null));

    if (MOCK_MODE() && !sessionRef.current) return;

    const session = sessionRef.current;
    const sdh = session?.sessionDescriptionHandler as Web.SessionDescriptionHandler | undefined;
    const pc = sdh?.peerConnection;
    if (!pc) return;
    pc.getSenders().forEach((sender) => {
      if (sender.track && sender.track.kind === 'audio') {
        sender.track.enabled = !newMuted;
      }
    });
  }, [call]);

  const toggleHold = useCallback(async () => {
    if (!call) return;

    // Curto-circuito: re-INVITE anterior ainda em flight. Evita double-click rapido
    // disparar duas requests de hold concorrentes (SIP.js queueia mas o estado
    // visual pode dessincronizar).
    if (pendingHoldRef.current) {
      // eslint-disable-next-line no-console
      console.warn('[webphone] toggleHold: re-INVITE em flight, ignorando click');
      return;
    }

    const newOnHold = !call.onHold;
    const session = sessionRef.current;

    // Mock mode (ou sem sessao ativa): so toggle visual.
    if (MOCK_MODE() && !session) {
      setCall((c) => (c ? { ...c, onHold: newOnHold } : null));
      return;
    }

    // Sem sessao SIP: so visual (defensivo, nao deveria acontecer com call != null).
    if (!session) {
      setCall((c) => (c ? { ...c, onHold: newOnHold } : null));
      return;
    }

    // Sessao nao Established: re-INVITE invalido. So visual.
    if (session.state !== SessionState.Established) {
      setCall((c) => (c ? { ...c, onHold: newOnHold } : null));
      return;
    }

    pendingHoldRef.current = true;
    let reInviteOk = false;
    try {
      // Approach 1: tentar re-INVITE via session.invite() com hold flag.
      // SIP.js v0.21 NAO expoe `hold` em SessionDescriptionHandlerOptions publicamente,
      // mas o Web.SessionDescriptionHandler interno respeita essa flag pra gerar SDP
      // com a=sendonly. Cast `as never` pra contornar o type check.
      // Funciona pra Inviter (outbound) e Invitation (inbound) — ambas tem invite()
      // pos-Established que dispara re-INVITE.
      try {
        const sessionWithInvite = session as unknown as {
          invite: (opts: {
            sessionDescriptionHandlerOptions?: unknown;
          }) => Promise<unknown>;
        };
        if (typeof sessionWithInvite.invite === 'function') {
          await sessionWithInvite.invite({
            sessionDescriptionHandlerOptions: { hold: newOnHold } as never,
          });
          reInviteOk = true;
        }
      } catch (reInviteErr) {
        // eslint-disable-next-line no-console
        console.warn('[webphone] re-INVITE falhou, caindo no fallback de track', reInviteErr);
      }

      // Approach 2 (fallback): mutar tracks no nivel de media.
      // Nao e hold "de verdade" (Asterisk nao detecta sendonly, MOH nao toca pro cliente),
      // mas garante que o agent nao manda audio pra ninguem. Sempre aplicar pra
      // sincronizar local mesmo com re-INVITE OK (re-INVITE so muda direcao SDP,
      // o sender track precisa estar coerente).
      const sdh = session.sessionDescriptionHandler as Web.SessionDescriptionHandler | undefined;
      const pc = sdh?.peerConnection;
      if (pc) {
        pc.getSenders().forEach((sender) => {
          if (sender.track && sender.track.kind === 'audio') {
            sender.track.enabled = !newOnHold;
          }
        });
      }

      // eslint-disable-next-line no-console
      console.log('[webphone] toggleHold ->', newOnHold, 'reInviteOk:', reInviteOk);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[webphone] hold toggle erro inesperado', err);
    } finally {
      // Sempre sincroniza estado visual, mesmo em erro (UX consistente).
      setCall((c) => (c ? { ...c, onHold: newOnHold } : null));
      pendingHoldRef.current = false;
    }
  }, [call]);

  const sendDtmf = useCallback((digit: string) => {
    if (stateRef.current !== 'in_call') return;

    if (MOCK_MODE() && !sessionRef.current) {
      // eslint-disable-next-line no-console
      console.log('[webphone mock] DTMF', digit);
      return;
    }

    const session = sessionRef.current;
    const sdh = session?.sessionDescriptionHandler as Web.SessionDescriptionHandler | undefined;
    if (!sdh || typeof sdh.sendDtmf !== 'function') return;
    sdh.sendDtmf(digit);
  }, []);

  // Ramal local — extrai do username das credenciais (formato "1001" ou
  // "1001@dominio"). Disponível assim que registramos no SIP.
  const extension = credsRef.current?.username?.split('@')[0] ?? null;

  return {
    state,
    call,
    error,
    extension,
    connect,
    disconnect,
    dial,
    acceptIncoming,
    rejectIncoming,
    hangup,
    toggleMute,
    toggleHold,
    sendDtmf,
    simulateIncoming,
  };
}
